import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from '../../../hooks'
import * as Styled from './styles'
import { SimpleSelect } from '../../selects/SimpleSelect/SimpleSelect'

type SearchOption = {
  id: string
  title: string
  placeholder?: string
}

interface Props {
  defaultOption?: string
  options: SearchOption[]
  className?: string
  onChange: (value: string, option: string) => void
  optionValue?: string
  value?: string
}

export const AdvancedSearchField: React.FC<Props> = ({
  options,
  onChange,
  defaultOption,
  className,
  optionValue,
  value = '',
}: Props) => {
  const [search, setSearch] = useState(value)
  const [option, setOption] = useState<string | number | undefined>(optionValue || defaultOption || options[0].id)
  const initial = useRef(!search)
  const debouncedSearch = useDebounce(search, 500)

  const onInputChange = useCallback(event => {
    setSearch(event.target.value)
  }, [])

  const onSelectChange = useCallback(id => {
    setOption(id)
  }, [])

  const onClear = useCallback(() => {
    setSearch('')
  }, [])

  useEffect(() => {
    if (!initial.current && option !== optionValue) setOption(optionValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionValue])

  useEffect(() => {
    if (!initial.current && search !== value) setSearch(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (!initial.current && (search !== value || option !== optionValue)) {
      onChange(debouncedSearch || '', option as string)
    } else {
      initial.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, option])

  const currentOption = useMemo(() => options.find(item => item.id === option), [option, options])

  return (
    <Styled.Wrapper className={className}>
      <SimpleSelect options={options} value={option} onChange={onSelectChange} customStyles={Styled.customStyles} />
      <Styled.Input
        placeholder={currentOption?.placeholder || 'default placeholder'}
        value={search}
        onChange={onInputChange}
      />
      <Styled.ClearIcon onClick={onClear} />
    </Styled.Wrapper>
  )
}
